import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { useSendCode, useVerifyCode } from '@/hooks/useAuth';
import { parseAuthError } from '@/api/auth';

export function OtpPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const phone = (location.state as { phone?: string } | null)?.phone ?? '';

  const [digits, setDigits] = useState<string[]>(['', '', '', '', '', '']);
  const [error, setError] = useState<string | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(60);
  const inputs = useRef<Array<HTMLInputElement | null>>([]);
  const verify = useVerifyCode();
  const resend = useSendCode();

  useEffect(() => {
    if (!phone) navigate('/login', { replace: true });
  }, [phone, navigate]);

  useEffect(() => {
    if (secondsLeft <= 0) return;
    const id = window.setInterval(() => setSecondsLeft((s) => s - 1), 1000);
    return () => window.clearInterval(id);
  }, [secondsLeft]);

  const submit = async (code: string) => {
    setError(null);
    try {
      const res = await verify.mutateAsync({ phone, code });
      navigate(res.is_new ? '/onboarding' : '/', { replace: true });
    } catch (err) {
      const parsed = parseAuthError(err);
      setError(parsed?.message ?? t('web.errors.generic'));
      setDigits(['', '', '', '', '', '']);
      inputs.current[0]?.focus();
    }
  };

  const handleChange = (index: number, value: string) => {
    const digit = value.replace(/\D/g, '').slice(-1);
    const next = [...digits];
    next[index] = digit;
    setDigits(next);
    if (digit && index < 5) inputs.current[index + 1]?.focus();
    if (next.every((d) => d.length === 1)) void submit(next.join(''));
  };

  const resendCode = async () => {
    await resend.mutateAsync(phone);
    setSecondsLeft(60);
  };

  return (
    <Card title={t('auth.enter_code')}>
      <p className="mb-4 text-sm text-slate-600">
        {t('auth.code_sent_to', { phone })}
      </p>
      <div className="mb-4 flex gap-2">
        {digits.map((d, i) => (
          <input
            key={i}
            ref={(el) => {
              inputs.current[i] = el;
            }}
            type="tel"
            inputMode="numeric"
            maxLength={1}
            value={d}
            onChange={(e) => handleChange(i, e.target.value)}
            className="h-12 w-12 rounded-lg border border-slate-200 text-center text-lg focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand-50"
            autoFocus={i === 0}
          />
        ))}
      </div>
      {error ? <p className="mb-3 text-sm text-red-600">{error}</p> : null}
      <Button
        type="button"
        variant="ghost"
        className="w-full"
        onClick={() => void resendCode()}
        disabled={secondsLeft > 0}
      >
        {secondsLeft > 0
          ? `${t('auth.resend_in')} ${secondsLeft}s`
          : t('auth.resend_code')}
      </Button>
    </Card>
  );
}
